/**
 * Tiered Shift Status Service
 * Implements 3-tier approach for shift status calculation:
 * 1. Historical shifts (past, not today): Stored status only
 * 2. Today's shifts: Full dynamic calculation
 * 3. Future shifts (not today): Simple assigned/unassigned logic
 */

import { parseISO, isToday, isPast, addDays, startOfDay, endOfDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import {
  ShiftStatus,
  ShiftWithEmployee,
  ShiftStatusBusinessRules,
  DEFAULT_BUSINESS_RULES
} from './shift-status-types';
import {
  defaultShiftStatusService,
  createShiftStatusService,
  ShiftStatusResult
} from './shift-status-service';
import { convertUtcDateToBusinessTimezone } from '../core/timezone-utils';

// Tier classification
export enum ShiftTier {
  HISTORICAL = 'historical',    // Past shifts (not today)
  TODAY = 'today',             // Today's shifts
  FUTURE = 'future'            // Future shifts (not today)
}

// Configuration options
export interface TieredShiftStatusOptions {
  businessId: string;
  businessTimezone?: string;
  businessRules?: Partial<ShiftStatusBusinessRules>;
  forceRealTime?: boolean;
  forceRecalculation?: boolean; // Force recalculation even for future shifts
}

// Extended shift interface with stored status
export interface ShiftWithStoredStatus extends ShiftWithEmployee {
  stored_status?: ShiftStatus | null;
  calculated_at?: string | null;
}

// Valid statuses for each tier
export const VALID_STATUSES_BY_TIER: Record<ShiftTier, ShiftStatus[]> = {
  [ShiftTier.HISTORICAL]: [
    ShiftStatus.COMPLETED,
    ShiftStatus.COMPLETED_LATE,
    ShiftStatus.MISSED,
    ShiftStatus.ON_TIME,
    ShiftStatus.LATE,
    ShiftStatus.TOO_EARLY,
    ShiftStatus.LEFT_EARLY,
    ShiftStatus.OVERTIME,
    ShiftStatus.NO_DEPARTURE_SCAN
  ],
  [ShiftTier.TODAY]: [
    // All statuses are valid for today's shifts
    ...Object.values(ShiftStatus)
  ],
  [ShiftTier.FUTURE]: [
    ShiftStatus.PENDING,   // For future shifts with employee assigned
    ShiftStatus.UNASSIGNED, // For future shifts without employee
    ShiftStatus.PLANNING   // For template-based planning
  ]
};

/**
 * Main Tiered Shift Status Service
 */
export class TieredShiftStatusService {
  private realTimeService: typeof defaultShiftStatusService;
  private businessRules: ShiftStatusBusinessRules;
  private businessTimezone: string;

  constructor(businessRules: Partial<ShiftStatusBusinessRules> = {}, businessTimezone: string = 'UTC') {
    this.businessRules = { ...DEFAULT_BUSINESS_RULES, ...businessRules };
    this.businessTimezone = businessTimezone;
    this.realTimeService = createShiftStatusService(this.businessRules, this.businessTimezone);
  }

  /**
   * Get shift status using the 3-tier approach
   */
  async getShiftStatus(
    shift: ShiftWithStoredStatus,
    options: TieredShiftStatusOptions
  ): Promise<ShiftStatusResult> {
    // If forceRecalculation is requested, use real-time calculation regardless of tier
    if (options.forceRecalculation) {
      return this.realTimeService.calculateDetailedShiftStatus(
        shift.start_time,
        shift.end_time,
        shift.employee_id,
        shift.arrival_time,
        shift.departure_time
      );
    }

    const tier = this.getShiftTier(shift.start_time, options.businessTimezone || this.businessTimezone);

    switch (tier) {
      case ShiftTier.HISTORICAL:
        return this.getHistoricalShiftStatus(shift);

      case ShiftTier.TODAY:
        return this.getTodayShiftStatus(shift, options);

      case ShiftTier.FUTURE:
        return this.getFutureShiftStatus(shift);

      default:
        console.warn(`Unknown shift tier: ${tier}. Falling back to real-time calculation.`);
        return this.realTimeService.calculateDetailedShiftStatus(
          shift.start_time,
          shift.end_time,
          shift.employee_id,
          shift.arrival_time,
          shift.departure_time
        );
    }
  }

  /**
   * Batch process multiple shifts with optimal performance
   */
  async batchGetShiftStatuses(
    shifts: ShiftWithStoredStatus[],
    options: TieredShiftStatusOptions
  ): Promise<Map<string, ShiftStatusResult>> {
    const results = new Map<string, ShiftStatusResult>();
    const effectiveTimezone = options.businessTimezone || this.businessTimezone;

    // Group shifts by tier for optimized processing
    const shiftsByTier = this.groupShiftsByTier(shifts, effectiveTimezone);

    // Process historical shifts (database only)
    for (const shift of shiftsByTier.historical) {
      const result = this.getHistoricalShiftStatus(shift);
      results.set(shift.id, result);
    }

    // Process future shifts (simple logic)
    for (const shift of shiftsByTier.future) {
      const result = this.getFutureShiftStatus(shift);
      results.set(shift.id, result);
    }

    // Process today's shifts (full calculation)
    const todayUpdates: { id: string; status: ShiftStatus }[] = [];
    for (const shift of shiftsByTier.today) {
      const result = await this.getTodayShiftStatus(shift, options);
      results.set(shift.id, result);

      // Queue for status update if different from stored
      if (shift.stored_status !== result.status) {
        todayUpdates.push({ id: shift.id, status: result.status });
      }
    }

    // Batch update today's shift statuses
    if (todayUpdates.length > 0) {
      await this.batchUpdateStoredStatuses(todayUpdates);
    }

    return results;
  }

  /**
   * Determine which tier a shift belongs to based on its date
   */
  public getShiftTier(shiftStartTime: string | Date, businessTimezone?: string): ShiftTier {
    const effectiveTimezone = businessTimezone || this.businessTimezone;
    const shiftDate = this.parseTimestampWithTimezone(shiftStartTime, effectiveTimezone);
    const currentTime = convertUtcDateToBusinessTimezone(new Date(), effectiveTimezone);

    // Check if shift is today in business timezone
    if (isToday(shiftDate)) {
      return ShiftTier.TODAY;
    }

    // Check if shift is in the past
    if (isPast(endOfDay(shiftDate))) {
      return ShiftTier.HISTORICAL;
    }

    // Otherwise, it's a future shift
    return ShiftTier.FUTURE;
  }

  /**
   * Get status for historical shifts (Tier 1)
   * Uses only stored status from database
   */
  private getHistoricalShiftStatus(shift: ShiftWithStoredStatus): ShiftStatusResult {
    // Use stored status if available
    if (shift.stored_status && this.isValidStatusForTier(shift.stored_status, ShiftTier.HISTORICAL)) {
      return { status: shift.stored_status };
    }

    // Fallback for missing historical status
    // Note: This should rarely occur after initial migration. Log for debugging if needed.
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Historical shift ${shift.id} missing valid stored status. Using fallback.`);
    }

    if (!shift.employee_id) {
      return { status: ShiftStatus.UNASSIGNED };
    }

    // Default historical fallback based on presence of time logs
    if (shift.arrival_time) {
      return { status: ShiftStatus.COMPLETED };
    } else {
      return { status: ShiftStatus.MISSED };
    }
  }

  /**
   * Get status for today's shifts (Tier 2)
   * Uses full dynamic calculation with real-time service
   */
  private async getTodayShiftStatus(
    shift: ShiftWithStoredStatus,
    options: TieredShiftStatusOptions
  ): Promise<ShiftStatusResult> {
    // Use full real-time calculation for today's shifts
    const result = this.realTimeService.calculateDetailedShiftStatus(
      shift.start_time,
      shift.end_time,
      shift.employee_id,
      shift.arrival_time,
      shift.departure_time
    );

    return result;
  }

  /**
   * Get status for future shifts (Tier 3)
   * Uses simple pending/unassigned logic with immediate assignment detection
   */
  private getFutureShiftStatus(shift: ShiftWithStoredStatus): ShiftStatusResult {
    // For future shifts, status depends purely on employee assignment
    if (shift.employee_id && shift.employee_id !== null) {
      // Shift is assigned to an employee -> PENDING status
      return { status: ShiftStatus.PENDING };
    } else {
      // Shift has no employee assigned -> UNASSIGNED status
      return { status: ShiftStatus.UNASSIGNED };
    }
  }

  /**
   * Group shifts by tier for batch processing
   */
  private groupShiftsByTier(
    shifts: ShiftWithStoredStatus[],
    businessTimezone: string
  ): Record<string, ShiftWithStoredStatus[]> {
    const grouped: Record<string, ShiftWithStoredStatus[]> = {
      historical: [],
      today: [],
      future: []
    };

    for (const shift of shifts) {
      const tier = this.getShiftTier(shift.start_time, businessTimezone);
      grouped[tier].push(shift);
    }

    return grouped;
  }

  /**
   * Check if a status is valid for a given tier
   */
  public isValidStatusForTier(status: ShiftStatus, tier: ShiftTier): boolean {
    return VALID_STATUSES_BY_TIER[tier].includes(status);
  }

  /**
   * Parse timestamp with timezone handling
   */
  private parseTimestampWithTimezone(timestamp: string | Date, businessTimezone: string): Date {
    try {
      const utcDate = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return convertUtcDateToBusinessTimezone(utcDate, businessTimezone);
    } catch (error) {
      console.warn(`Failed to parse timestamp ${timestamp}:`, error);
      return new Date(); // Fallback to current time
    }
  }

  /**
   * Batch update stored statuses for multiple shifts
   */
  private async batchUpdateStoredStatuses(
    updates: { id: string; status: ShiftStatus }[]
  ): Promise<void> {
    if (updates.length === 0) return;

    try {
      const { error } = await supabase
        .from('shifts')
        .upsert(
          updates.map(update => ({
            id: update.id,
            status: update.status,
            calculated_at: new Date().toISOString()
          })),
          { onConflict: 'id' }
        );

      if (error) {
        console.warn('Failed to batch update stored statuses:', error);
      }
    } catch (error) {
      console.warn('Error in batch status update:', error);
    }
  }

  /**
   * Recalculate and store status for specific shifts (maintenance operation)
   */
  async recalculateAndStore(shiftIds: string[], businessId: string): Promise<void> {
    const { data: shifts, error } = await supabase
      .from('shifts')
      .select(`
        id,
        start_time,
        end_time,
        employee_id,
        business_id
      `)
      .in('id', shiftIds)
      .eq('business_id', businessId);

    if (error) {
      throw new Error(`Failed to fetch shifts for recalculation: ${error.message}`);
    }

    if (!shifts || shifts.length === 0) {
      return;
    }

    const updates: { id: string; status: ShiftStatus }[] = [];

    for (const shift of shifts) {
      const tier = this.getShiftTier(shift.start_time, this.businessTimezone);

      if (tier === ShiftTier.TODAY || tier === ShiftTier.HISTORICAL) {
        // Only recalculate for shifts that should have stored status
        const result = this.realTimeService.calculateDetailedShiftStatus(
          shift.start_time,
          shift.end_time,
          shift.employee_id,
          null, // Would need to fetch time logs separately
          null
        );

        updates.push({ id: shift.id, status: result.status });
      }
    }

    if (updates.length > 0) {
      await this.batchUpdateStoredStatuses(updates);
    }
  }

  /**
   * Get statistics about the tiered system usage
   */
  async getTierStats(businessId: string): Promise<{
    totalShifts: number;
    historicalShifts: number;
    todayShifts: number;
    futureShifts: number;
    shiftsWithStoredStatus: number;
  }> {
    const { data: shifts, error } = await supabase
      .from('shifts')
      .select('id, start_time, status')
      .eq('business_id', businessId);

    if (error || !shifts) {
      return {
        totalShifts: 0,
        historicalShifts: 0,
        todayShifts: 0,
        futureShifts: 0,
        shiftsWithStoredStatus: 0
      };
    }

    const stats = shifts.reduce((acc, shift) => {
      acc.totalShifts++;

      const tier = this.getShiftTier(shift.start_time, this.businessTimezone);
      acc[`${tier}Shifts`]++;

      if (shift.status) {
        acc.shiftsWithStoredStatus++;
      }

      return acc;
    }, {
      totalShifts: 0,
      historicalShifts: 0,
      todayShifts: 0,
      futureShifts: 0,
      shiftsWithStoredStatus: 0
    });

    return stats;
  }

  /**
   * Force status recalculation for a single shift, bypassing tier restrictions
   * Useful for immediate updates after employee assignment changes
   */
  async forceStatusRecalculation(
    shift: ShiftWithStoredStatus,
    options: Omit<TieredShiftStatusOptions, 'forceRecalculation'>
  ): Promise<ShiftStatusResult> {
    return this.getShiftStatus(shift, {
      ...options,
      forceRecalculation: true
    });
  }
}

// Export factory function for custom configurations
export const createTieredShiftStatusService = (
  businessRules?: Partial<ShiftStatusBusinessRules>,
  businessTimezone?: string
): TieredShiftStatusService => {
  return new TieredShiftStatusService(businessRules, businessTimezone);
};